// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cludoSeachStyles } from "@awc/App/ComponentExtensions/Common/CludoSearchComponentExtensions";
import { StyledOverlayComponent } from "@awc/App/Components/Common/StyledOverlay";
import { menuBodyStyle } from "@awc/App/Components/Menu/DropdownMenu";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { color, getSystemColor, hideElementOnPrint } from "@awc/XDL/Utilities";
import { StyledTypography } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const zIndexLoadingOverlay = 1500;

export const hideLoadingOverlay = css`
    #react-app:not(.allow-loadingOverlay) {
        svg[class^="LoadingSpinnerStyle"],
        div[class^="LoadingOverlayStyle"] {
            visibility: hidden;
        }
    }
`;

export const xngLoadingOverlayUtilitiesStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

export default {
    css: css`
        ${hideLoadingOverlay}
        ${xngLoadingOverlayUtilitiesStyles}
        .enlarged-img {
            z-index: 1;
            background: ${getSystemColor("common.white")};
        }
        body {
            div[data-test-selector="page_ProductListPage"] div[class^="PageLayoutWrapper"] > main,
            div[data-test-selector="page_CategoryDetailsPage"] div[class^="PageLayoutWrapper"] > main,
            div[data-test-selector="page_ProductDetailsPage"] div[class^="PageLayoutWrapper"] > main {
                min-height: 80vh;
            }
            &:after {
                transition: all 0.2s linear;
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                z-index: ${getProp("theme.zIndex.shade")};
                width: 100%;
                height: 100%;
                pointer-events: none;
            }
            &.shade {
                &:after {
                    background-color: ${color("common.ui7", 0.7)};
                }
            }
            &.drawer-open ${StyledOverlayComponent} {
                z-index: ${getProp("theme.zIndex.header")};
            }

            .count-text {
                font-weight: ${fontConfig.weight.medium};
            }
            ${cludoSeachStyles}
        }

        .custom-cable-design strong {
            color: ${color("common.ui14")};
        }
        #cludo-search-form-not-found input[type="search"] {
            border: 1px solid transparent;
            background-color: ${color("common.ui16")};
            color: ${color("common.ui23")};
            padding: 0 40px 0 40px;
            height: 40px;
            line-height: ${fontConfig.lineHeight.singleLineRegular};
            width: 92%;
            font-size: ${fontConfig.fontSize.small}px;
            -webkit-appearance: none;
            display: inline-block;
            margin: 0;
        }

        .error-details-container .search-container label {
            display: none;
        }

        .search-input-header {
            max-width: 600px;
            width: 100%;
        }

        .search-input-header:before {
            content: "\\e001";
            position: relative;
            font-family: "good-oatmeal-v2";
            display: inline-block;
            color: ${color("common.ui14")};
            text-indent: 0;
            font-size: ${fontConfig.fontSize.singleLineRegular}px;
            left: 35px;
            top: 4px;
        }

        a.awc-yellow-solid {
            background-color: ${color("common.ui14")};
            border-radius: 12px;
            width: 250px;
            padding: 16px 32px;
            margin: 0 0 20px;
            display: inline-block;
            text-align: center;
            span {
                font-size: ${fontConfig.fontSize.link}px;
                font-weight: ${fontConfig.weight.bold};
                color: ${color("common.white")} !important;
            }
        }

        a.awc-yellow-outline {
            background-color: transparent;
            border: 2px solid ${color("common.ui14")};
            border-radius: 12px;
            width: 250px;
            padding: 16px 32px;
            margin: 0 0 20px;
            display: inline-block;
            text-align: center;
            span {
                font-size: ${fontConfig.fontSize.singleLineRegular}px;
                font-weight: ${fontConfig.weight.bold};
                color: ${color("common.ui14")} !important;
            }
            &:hover span {
                text-decoration: underline;
            }
        }

        table.awc-table-blue {
            border-collapse: collapse;
            border: solid ${color("primary")} 3px;
            border-radius: 6px;
            -moz-border-radius: 6px;
            display: inline-block;
            vertical-align: top;
        }
        table.awc-table-blue tr:nth-of-type(odd) {
            background-color: ${color("common.ui15")};
            border: none;
        }
        table.awc-table-blue a {
            color: ${color("primary")};
            text-decoration: underline;
            font-weight: ${fontConfig.weight.bold};
        }
        table.awc-table-blue td {
            color: ${color("common.ui29")};
        }
        table.awc-table-blue td strong,
        table.awc-table-blue td b,
        table.awc-table-blue td h3,
        table.awc-table-blue td h1,
        table.awc-table-blue td h2,
        table.awc-table-blue td h3 h4,
        table.awc-table-blue td h5,
        table.awc-table-blue td h6,
        table.awc-table-blue td p,
        table.awc-table-blue td span {
            color: ${color("common.ui29")};
        }
        table.awc-table-blue th {
            background: ${color("primary")};
            color: ${color("common.white")};
            border-top: none;
        }
        table.awc-table-blue th strong,
        table.awc-table-blue th b,
        table.awc-table-blue th h3,
        table.awc-table-blue th h1,
        table.awc-table-blue th h2,
        table.awc-table-blue th h3 h4,
        table.awc-table-blue th h5,
        table.awc-table-blue th h6 {
            color: ${color("common.white")};
        }
        table.awc-table-blue td:first-child,
        table.awc-table-blue th:first-child {
            border-left: none;
        }

        .button {
            background-color: ${color("primary.main")};
            border: 2px solid ${color("primary.main")};
            color: ${color("common.white")};
            display: inline-flex;
            padding: 0 24px;
            height: 32px;
            font-size: ${fontConfig.fontSize.singleLineRegular}px;
            border-radius: 8px;

            && span {
                color: ${color("common.white")};
            }

            &&:hover {
                background-color: ${color("common.ui8")};
                color: ${color("common.white")};
                border: 2px solid ${color("common.ui8")};

                && span {
                    color: ${color("common.white")};
                }
            }

            && :focus {
                background-color: ${color("common.ui9")};
                border: 2px solid ${color("common.ui9")};
            }
        }

        // This style to fix an issue with Google Places autocomplete
        .pac-container {
            z-index: 10000 !important;
        }

        .date-picker-wrapper {
            display: flex;
        }

        ${menuBodyStyle}

        // used in rich-content of header in the top logo nav.
        .header-brandlogo {
            a {
                border-right: 1px solid ${color("common.border")};
                &:hover,
                &:focus {
                    background-color: ${color("common.ui15")};
                }
            }
        }
        /* used in Allied university Links authoring so that link font size doesnt override Heading font size inside it */
        .auOptionContainer ${StyledTypography} {
            font-size: inherit;
        }
        // customer service page
        .node {
            color: ${color("common.ui14")} !important;
            text-decoration: none;
            cursor: pointer;
        }
        .nav_section a {
            ${TypographyStyle} {
                color: ${color("common.ui4")};
            }
            display: block;
        }

        // hide cookies widget on print
        #onetrust-consent-sdk {
            ${hideElementOnPrint}
        }

        figure {
            margin: 0; /* remove default browser margin */
        }
    `,
} as xBaseTheme["globalStyle"];
